import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowSquareOutIcon,
  ArrowsClockwiseIcon,
  LinkIcon,
  PlusIcon,
  TrashIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { ApiError, Button, Input, StatusChip, fmtRelativeTime, type Tone } from '@smena/ui';
// Через подпуть, а не через бочку: `@smena/core` тянет node:fs/node:crypto и не собирается
// для браузера (тот же приём, что у `@smena/core/labels`).
import { displayUrl, toAbsoluteUrl } from '@smena/core/listing-url';
import {
  addPropertySource,
  fetchPropertySources,
  rcObjectUrl,
  refreshPropertySource,
  removePropertySource,
  type PropertySource,
} from './api';

/**
 * «Источники» в карточке объекта: ссылки на объявления, с которых тянутся данные.
 *
 * До этого ссылка была ровно одна, лежала в `data._source` и нигде не показывалась: УК не
 * видела, откуда вообще приехал объект, не могла перейти в объявление и не могла добавить
 * вторую площадку - одна квартира почти всегда висит и на Суточно, и на Островке.
 *
 * Переразбор трогает только публичные поля. Wi-Fi, код замка, домофон и экстренные телефоны
 * объявление не знает, и затирать их обновлением цены нельзя.
 */
export function PropertySourcesSection({
  propertyId,
  rcObjectId,
}: {
  propertyId: string;
  /** Лот в кабинете RealtyCalendar, если объект связан с ним (rc_object_mappings). */
  rcObjectId?: string | null;
}) {
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const [confirmUrl, setConfirmUrl] = useState<string | null>(null);
  const [busyUrl, setBusyUrl] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['property-sources', propertyId],
    queryFn: () => fetchPropertySources(propertyId),
  });

  function invalidate(): void {
    void qc.invalidateQueries({ queryKey: ['property-sources', propertyId] });
    void qc.invalidateQueries({ queryKey: ['properties'] });
  }

  const addMut = useMutation({
    mutationFn: (u: string) => addPropertySource(propertyId, u),
    onSuccess: (r) => {
      setUrl('');
      if (r.rcObjectId) {
        toast.success(`Объект связан с RealtyCalendar (лот ${r.rcObjectId}) - брони будут приходить сюда`);
        invalidate();
        return;
      }
      const last = r.items[r.items.length - 1];
      if (last?.lastStatus === 'error') {
        toast.error(`Ссылку привязали, но объявление не открылось: ${last.lastError ?? 'ошибка'}`);
      } else {
        toast.success(r.applied ? 'Ссылка привязана, анкета обновлена' : 'Ссылка привязана');
      }
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось привязать ссылку'),
  });

  const refreshMut = useMutation({
    mutationFn: (u: string) => refreshPropertySource(propertyId, u),
    onSuccess: (r) => {
      toast.success(r.applied ? 'Данные обновлены из объявления' : 'Объявление не открылось');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось обновить'),
    onSettled: () => setBusyUrl(null),
  });

  const removeMut = useMutation({
    mutationFn: (u: string) => removePropertySource(propertyId, u),
    onSuccess: () => {
      setConfirmUrl(null);
      toast.success('Ссылка отвязана');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось отвязать'),
  });

  // Принимаем три вещи: ссылку на объявление, ссылку на объект в RealtyCalendar и голый
  // номер лота RC (он подписан в карточке объекта их кабинета и его проще перепечатать,
  // чем выцеплять адрес SPA). Разбор всё равно делает сервер - здесь только гейт кнопки.
  const trimmed = url.trim();
  const urlValid = /^https?:\/\//i.test(trimmed) || /^\d{1,12}$/.test(trimmed) || /realtycalendar\.ru/i.test(trimmed);
  const items = q.data ?? [];

  return (
    <div className="flex flex-col gap-3 rounded-md border border-line bg-panel2/40 p-4">
      <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted">
        <LinkIcon weight="bold" className="size-4 text-accent-fg" /> Источники
      </div>

      <p className="text-xs text-muted">
        Ссылки на объявления этой квартиры. По ним обновляются описание, фото, цена и правила -
        Wi-Fi, коды замков и телефоны из объявления не берутся и при обновлении не затираются.
      </p>
      {!rcObjectId ? (
        <p className="text-xs text-muted">
          Сюда же можно вставить ссылку на эту квартиру в кабинете RealtyCalendar (вида{' '}
          <span className="text-text">realtycalendar.ru/apartment/#/address/357374/</span>) или
          просто номер её лота - тогда брони по ней начнут приходить сразу, не дожидаясь первой
          из них.
        </p>
      ) : null}

      {/* Связь с RealtyCalendar - тоже источник, но другого рода: оттуда приходят брони,
          а не содержимое карточки. Поэтому отдельной строкой и без кнопок «Обновить»
          и «Отвязать»: отвязка убила бы поток броней, это действие не отсюда. */}
      {rcObjectId ? (
        <div className="flex flex-col gap-1 rounded-md border border-line bg-card p-3">
          <div className="flex flex-wrap items-center gap-2">
            <StatusChip tone="accent">брони</StatusChip>
            <span className="text-xs text-muted">realtycalendar</span>
          </div>
          <a
            href={rcObjectUrl(rcObjectId)}
            target="_blank"
            rel="noopener noreferrer"
            title={`Открыть объект ${rcObjectId} в кабинете RealtyCalendar`}
            className="mt-1 flex items-start gap-1 text-xs text-accent-fg underline decoration-dotted underline-offset-2 break-all"
          >
            RealtyCalendar · лот {rcObjectId}
            <ArrowSquareOutIcon weight="bold" className="mt-0.5 size-3 shrink-0" />
          </a>
          <div className="mt-1 text-[11px] text-muted">
            Брони по этому объекту приезжают сюда сами. Содержимое карточки RealtyCalendar не
            заполняет - Wi-Fi, замок и правила вносите здесь.
          </div>
        </div>
      ) : null}

      {q.isLoading ? (
        <p className="text-xs text-muted">Загружаем…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted">
          {rcObjectId
            ? 'Ссылок на объявления пока нет. Привяжите объявление, чтобы подтягивать описание, фото и цену.'
            : 'Пока ни одной. Объект заведён вручную - привяжите ссылку, чтобы подтягивать данные.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((s) => (
            <li key={s.url} className="flex flex-col gap-2 rounded-md border border-line bg-card p-3">
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusChip tone={tone(s)}>{label(s)}</StatusChip>
                    {s.platform ? <span className="text-xs text-muted">{s.platform}</span> : null}
                  </div>
                  <a
                    href={toAbsoluteUrl(s.url) ?? s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 flex items-start gap-1 text-xs text-accent-fg underline decoration-dotted underline-offset-2 break-all"
                  >
                    {displayUrl(s.url)}
                    <ArrowSquareOutIcon weight="bold" className="mt-0.5 size-3 shrink-0" />
                  </a>
                  <div className="mt-1 text-[11px] text-muted">
                    {s.lastParsedAt ? `Обновлено ${fmtRelativeTime(s.lastParsedAt)}` : 'Ещё не читали'}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busyUrl === s.url}
                    onClick={() => {
                      setBusyUrl(s.url);
                      refreshMut.mutate(s.url);
                    }}
                  >
                    <ArrowsClockwiseIcon weight="bold" className="size-4" />
                    {busyUrl === s.url ? 'Читаем…' : 'Обновить'}
                  </Button>
                  {confirmUrl !== s.url ? (
                    <Button variant="ghost" size="sm" onClick={() => setConfirmUrl(s.url)}>
                      <TrashIcon weight="bold" className="size-4" />
                    </Button>
                  ) : null}
                </div>
              </div>

              {s.lastError ? (
                <div className="flex items-start gap-2 rounded-md border border-amber/40 bg-amber/10 p-2 text-[11px] text-amber">
                  <WarningIcon weight="fill" className="mt-0.5 size-3.5 shrink-0" />
                  <span className="break-words">{s.lastError}</span>
                </div>
              ) : null}

              {confirmUrl === s.url ? (
                <div className="flex flex-col gap-2 border-t border-line pt-2">
                  <span className="text-xs text-amber">
                    Ссылка отвяжется, данные объекта останутся как есть - но обновлять их будет
                    неоткуда.
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={removeMut.isPending}
                      onClick={() => removeMut.mutate(s.url)}
                    >
                      {removeMut.isPending ? 'Отвязываем…' : 'Отвязать'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmUrl(null)}>
                      Отмена
                    </Button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (!urlValid || addMut.isPending) return;
          addMut.mutate(url.trim());
        }}
      >
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Ссылка на объявление, на объект в RealtyCalendar или номер лота"
          autoComplete="off"
          spellCheck={false}
        />
        <Button type="submit" size="sm" disabled={!urlValid || addMut.isPending} className="shrink-0">
          <PlusIcon weight="bold" className="size-4" />
          {addMut.isPending ? 'Читаем объявление…' : 'Привязать'}
        </Button>
      </form>
      {url.trim() && !urlValid ? (
        <span className="text-[11px] text-red">
          Нужна ссылка (http:// или https://) либо номер лота RealtyCalendar
        </span>
      ) : null}
    </div>
  );
}

function tone(s: PropertySource): Tone {
  if (s.lastStatus === 'ok') return 'green';
  if (s.lastStatus === 'error') return 'amber';
  return 'muted';
}

function label(s: PropertySource): string {
  if (s.lastStatus === 'ok') return 'читается';
  if (s.lastStatus === 'error') return 'не открылась';
  return 'не читали';
}
